import { useMemo } from "react"
import { ApiError } from "../../shared/api/request.js"
import { useFilterStore } from "../filters/model/filter-store.js"
import { useTileQuery, type TileSpec } from "./hooks/use-tile-query.js"

/**
 * ⚠ **Three outcomes, and conflating any two of them is the whole risk here.**
 */

const IDENTITY_SPEC: TileSpec = {
  tileKey: "identity.users",
  metrics: ["username", "email_address", "user_role_name"],
  splitBy: ["user"],
}

export type ResolvedUser = { name: string; email?: string; role?: string }

export type UserNames = {
  label: ((raw: string) => string) | undefined
  lookup: (raw: string) => ResolvedUser | undefined
  forbidden: boolean
  loading: boolean
}

/**
 * ⚠ **"Deleted user" is our inference, not something srv sends.** srv has no such notion — it simply stops
 * listing an account, while the log entries that account produced stay where they are. The phrase is us
 * putting a name to that gap, and it should read as a statement about the customer's staff, not as a lookup
 * that failed.
 */
const deletedUser = (raw: string) => `Deleted user · ${raw.replace(/^u_/, "").slice(0, 6)}`

export function userLabeller(
  byPseudonym: ReadonlyMap<string, ResolvedUser>,
  forbidden: boolean,
): ((raw: string) => string) | undefined {
  if (forbidden || byPseudonym.size === 0) return undefined
  return (raw: string) => byPseudonym.get(raw)?.name ?? deletedUser(raw)
}

export function useUserNames(enabled: boolean): UserNames {
  const filters = useFilterStore((s) => s.filters)
  const query = useTileQuery(IDENTITY_SPEC, filters, enabled)

  const forbidden = query.error instanceof ApiError && query.error.status === 403

  return useMemo(() => {
    const byPseudonym = new Map<string, ResolvedUser>()
    /**
     * ⚠ **`enabled: false` stops the fetch, not the cache read.**
     */
    const result = enabled ? query.data : undefined

    if (result) {
      const userCol = result.columns.find((c) => c.role === "dimension" && c.key.endsWith(".user"))
      const col = (suffix: string) => result.columns.find((c) => c.key.endsWith(suffix))
      const nameCol = col(".username")
      const emailCol = col(".email_address")
      const roleCol = col(".user_role_name")

      if (userCol && nameCol) {
        for (const row of result.rows) {
          const id = String(row[userCol.key] ?? "")
          const name = row[nameCol.key]
          if (!id || name == null || name === "") continue
          const email = emailCol ? row[emailCol.key] : null
          const role = roleCol ? row[roleCol.key] : null
          byPseudonym.set(id, {
            name: String(name),
            ...(email != null && email !== "" ? { email: String(email) } : {}),
            ...(role != null && role !== "" ? { role: String(role) } : {}),
          })
        }
      }
    }

    const lookup = (raw: string) => byPseudonym.get(raw)

    return { label: userLabeller(byPseudonym, forbidden), lookup, forbidden, loading: query.isLoading }
  }, [enabled, query.data, query.isLoading, forbidden])
}
